import type { ParamMap, Router } from '@angular/router';
import { parseShareItemUuidFromRedirectUri, type ShareInfo } from '@mtm/auth';

/** `?step=` value on `/entry/login` that opens a share link's request-access form. */
export const SHARE_REQUEST_ACCESS_STEP: string = 'request-access';

/** Navigation-state key carrying a share description fetched on the way to the request-access form. */
const SHARE_INFO_STATE_KEY: string = 'shareInfo';

/**
 * Navigation state that hands `shareInfo` to the request-access form, so `mtm-auth` shows it instead of
 * fetching it again; `undefined` when there is nothing to hand over.
 */
export function shareInfoNavigationState(shareInfo: ShareInfo | null): Record<string, ShareInfo> | undefined {
  return shareInfo ? { [SHARE_INFO_STATE_KEY]: shareInfo } : undefined;
}

/**
 * The share description {@link shareInfoNavigationState} handed over, read while the navigation carrying it
 * is still running (a routed component's constructor). `null` on a reload or any other way in — `mtm-auth`
 * then fetches it itself.
 */
export function prefetchedShareInfo(router: Router): ShareInfo | null {
  return (router.currentNavigation()?.extras.state?.[SHARE_INFO_STATE_KEY] as ShareInfo | undefined) ?? null;
}

/**
 * Whether the query asks for a share link's request-access form: `?step=request-access` together with
 * the share link itself as `redirect_uri` — the item `mtm-auth` sends the request for.
 */
export function isShareRequestAccess(queryParams: ParamMap): boolean {
  return (
    queryParams.get('step') === SHARE_REQUEST_ACCESS_STEP &&
    parseShareItemUuidFromRedirectUri(queryParams.get('redirect_uri')) !== null
  );
}
